import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from './../Glob';
import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { IPayload } from '../actions/AppActions';

import {
    DialogActions,
    I,
    checkAction,
    DialogConstants,
    DialogDispatcher,
    IDialog,
    IPopup,
    IDialogConfig,
    IDialogAction,
    IPopupOptions
} from '../../../../pdc-platform/src/ts/actions/DialogActions';

const BASE_ZINDEX = 1300;

export interface IDialogsStoreState {
    dialogs: Map<string, IDialog>;
    popups: Map<string, IPopup>;
    maxDialogID: number;
    maxZIndex: number;
}

export class DialogsStore extends BaseStore<IDialogsStoreState, DialogActions, DialogDispatcher, IGlob> {
    public _state: IDialogsStoreState;
    public static _name = 'dialogs';

    constructor(glob: IGlob, dispatcher: DialogDispatcher, actions: DialogActions) {
        super(glob, dispatcher, actions, DialogsStore._name);

        this._state = _.extend(DialogsStore.getInitialState(), this._state);
    }

    static get(glob, dispatcher: DialogDispatcher, actions: DialogActions) {
        return new DialogsStore(glob, dispatcher, actions);
    }

    public static getInitialState(): IDialogsStoreState {
        return {
            dialogs: new Map<string, IDialog>(),
            popups: new Map<string, IPopup>(),
            maxDialogID: 0,
            maxZIndex: BASE_ZINDEX
        };
    }

    private createDialog(title: string, text: string, success, fail, cancel, type, config: IDialogConfig) {
        this._state.maxDialogID += 1;
        this._state.maxZIndex += 1;

        const newDialog: IDialog = {
            id: this._state.maxDialogID,
            zIndex: this._state.maxZIndex,
            title: title,
            text: text,
            success: success,
            fail: fail,
            cancel: cancel,
            type: type,
            config: config
        };

        newDialog.setConfirmValueGetter = (getter) => {
            newDialog.confirmValueGetter = getter;
        };

        newDialog.unsetConfirmValueGetter = (getter) => {
            delete newDialog.confirmValueGetter;
        };

        this._state.dialogs.set(newDialog.id.toString(), newDialog);
    }

    private activateDialog(id: string) {
        const obj = this._state.dialogs.get(id);

        if (obj == null) {
            return;
        }

        obj.active = true;
    }

    private adjustMaxZIndex() {
        let result = BASE_ZINDEX;

        this._state.dialogs.forEach((dialog) => {
            if (dialog.zIndex > result) {
                result = dialog.zIndex;
            }
        });

        this._state.popups.forEach((popup) => {
            if (popup.zIndex > result) {
                result = popup.zIndex;
            }
        });

        this._state.maxZIndex = result;
    }

    private destroyDialog(id: string) {
        this._state.dialogs.delete(id);
        this.adjustMaxZIndex();
    }

    private createPopup(type, options: IPopupOptions) {
        this._state.maxDialogID += 1;
        this._state.maxZIndex += 1;

        this._state.popups.set(this._state.maxDialogID.toString(), {
            id: this._state.maxDialogID,
            zIndex: this._state.maxZIndex,
            type: type,
            options: options
        });
        this.glob.AppActions.setLockEscape(true); // See Issue 96103.
    }

    private destroyPopup(id: string) {
        this._state.popups.delete(id);
        this.adjustMaxZIndex();
        this.glob.AppActions.setLockEscape(false);
    }

    protected eventsHandler(payload: IPayload<IDialogAction>) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const action = payload.action;

        if (checkAction<I.CREATE_DIALOG>(action, DialogConstants.CREATE_DIALOG)) {
            this.createDialog(
                action.title,
                action.text,
                action.success,
                action.fail,
                action.cancel,
                action.type,
                action.config
            );
            this.emitChange();
        } else if (checkAction<I.DESTROY_DIALOG>(action, DialogConstants.DESTROY_DIALOG)) {
            this.destroyDialog(action.id);
            this.emitChange();
        } else if (checkAction<I.ACTIVATE_DIALOG>(action, DialogConstants.ACTIVATE_DIALOG)) {
            this.activateDialog(action.id);
            this.emitChange();
        } else if (checkAction<I.CREATE_POPUP>(action, DialogConstants.CREATE_POPUP)) {
            this.createPopup(action.type, action.options);
            this.emitChange();
        } else if (checkAction<I.DESTROY_POPUP>(action, DialogConstants.DESTROY_POPUP)) {
            this.destroyPopup(action.id);
            this.emitChange();
        }

        return true;
    }

    public static init(glob: IGlob) {
        if (glob.DialogDispatcher == null) {
            glob.DialogDispatcher = DialogDispatcher.get();
        }

        if (glob.DialogActions == null) {
            glob.DialogActions = DialogActions.get(glob.DialogDispatcher);
        }

        if (glob.DialogsStore == null) {
            glob.DialogsStore = DialogsStore.get(glob, glob.DialogDispatcher, glob.DialogActions);
        }
    }
}
